import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import { getVideoById, getCategoryBySlug, categories, featuredVideos } from "@/mocks/videos";
import { getArticleById, getRelatedArticles, articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!id) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500">内容不存在</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const video = getVideoById(id);
  const article = getArticleById(id);

  const isVideoContent = !!video;
  const item = video || article;

  if (!item) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-question-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500">内容不存在</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySlug = "category" in item ? item.category : "slug" in item ? item.slug : "";
  const categoryInfo = getCategoryBySlug(categorySlug);
  const categoryName = categoryInfo?.name || categorySlug;

  let relatedItems;
  if (article) {
    relatedItems = getRelatedArticles(article);
  } else {
    relatedItems = articles.slice(0, 3);
  }

  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: item.title,
    description: "summary" in item ? item.summary : item.description,
    datePublished: item.publishDate,
    author: {
      "@type": "Organization",
      name: "author" in item ? item.author : "99视频编辑部",
    },
    publisher: {
      "@type": "Organization",
      name: "99视频",
      url: "https://99video.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://99video.com/article/${id}`,
    },
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: categoryName, href: `/category/${categorySlug}` },
            { label: item.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6" aria-label={item.title}>
          <div className="max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {("tags" in item ? item.tags : []).map((tag: string) => (
                  <span key={tag} className="px-2.5 py-0.5 text-xs rounded-full bg-accent-100 text-accent-700">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                <strong>{item.title}</strong>
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {item.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {item.views} 次阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {"author" in item ? item.author : "99视频编辑部"}
                </span>
                {"source" in item && item.source && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 text-xs">
                    <i className="ri-external-link-line"></i>
                    来源：{item.source}
                  </span>
                )}
              </div>
            </header>

            {isVideoContent && video && (
              <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-8 bg-background-900">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  title={`${video.title} - 99视频在线观看`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors">
                    <i className="ri-play-fill text-white text-3xl md:text-4xl"></i>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <span className="text-white text-sm font-medium">{video.title}</span>
                  <span className="text-white/80 text-xs bg-black/50 px-2 py-1 rounded">{video.duration}</span>
                </div>
              </div>
            )}

            <div className="prose max-w-none">
              <div
                className="text-foreground-700 leading-relaxed text-sm md:text-base"
                dangerouslySetInnerHTML={{
                  __html: "content" in item ? item.content : `<p>${item.description}</p>`,
                }}
              />
            </div>

            {isVideoContent && video && (
              <div className="mt-6 p-4 rounded-lg bg-background-100 border border-background-200">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-foreground-500">画质：</span>
                    <span className="text-foreground-800 font-medium">{video.quality}</span>
                  </div>
                  <div>
                    <span className="text-foreground-500">时长：</span>
                    <span className="text-foreground-800 font-medium">{video.duration}</span>
                  </div>
                  <div>
                    <span className="text-foreground-500">评分：</span>
                    <span className="text-accent-600 font-medium">
                      <i className="ri-star-fill mr-1"></i>
                      {video.rating}
                    </span>
                  </div>
                  <div>
                    <span className="text-foreground-500">来源：</span>
                    <span className="text-foreground-800 font-medium">{video.source}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </article>

        {(relatedItems.length > 0 || featuredVideos.length > 0) && (
          <section className="w-full px-4 md:px-6 py-10 bg-background-100" aria-label="相关推荐">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-lg font-bold text-foreground-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                相关推荐
              </h2>

              {isVideoContent ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-product-shop>
                  {featuredVideos
                    .filter((v) => v.id !== id)
                    .slice(0, 3)
                    .map((v) => (
                      <VideoCard key={v.id} video={v} />
                    ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedItems.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group block p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{rel.summary}</p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                        <span>{rel.publishDate}</span>
                        <span>{rel.views} 阅读</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        <section className="w-full px-4 md:px-6 py-10" aria-label="浏览更多分类">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg font-bold text-foreground-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              浏览更多分类
            </h2>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full bg-background-50 border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={cat.icon}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://99video.com/" },
            { "@type": "ListItem", position: 2, name: categoryName, item: `https://99video.com/category/${categorySlug}` },
            { "@type": "ListItem", position: 3, name: item.title },
          ],
        })}
      </script>
    </>
  );
}